/**
 * Utility functions for Matomo analytics
 */

import { getTestUserDomains, shouldFilterTestUsers } from "./config";

/**
 * Hash a user ID for privacy-compliant tracking
 * Uses a simple hash function to pseudonymize user IDs
 *
 * @param userId - The original user ID to hash
 * @returns A hashed version of the user ID
 */
export async function hashUserId(userId: string): Promise<string> {
	// Use the Web Crypto API for secure hashing
	const encoder = new TextEncoder();
	const data = encoder.encode(userId);
	const hashBuffer = await crypto.subtle.digest("SHA-256", data);

	// Convert buffer to hex string
	const hashArray = Array.from(new Uint8Array(hashBuffer));
	const hashHex = hashArray
		.map((b) => b.toString(16).padStart(2, "0"))
		.join("");

	// Return first 16 characters for a shorter, but still unique identifier
	return hashHex.substring(0, 16);
}

/**
 * Check if a user is a test/internal user based on email domain
 * Only applies filtering in production environment
 *
 * @param email - User's email address
 * @returns true if user should be treated as test user in current environment
 */
export function isTestUser(email: string | undefined): boolean {
	// Only filter in production
	if (!shouldFilterTestUsers()) {
		return false;
	}

	if (!email) {
		return false;
	}

	const testDomains = getTestUserDomains();
	const emailLower = email.toLowerCase();

	return testDomains.some((domain) =>
		emailLower.endsWith(domain.toLowerCase())
	);
}
